import * as React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import Layout from '../../components/layout';
import Seo from '../../components/seo';
import useStations from '../../utils/hooks/useStations';

import { content, data, diagrams } from '../../assets/markdown/details-sooty-blotch-flyspeck.md';

/** Flattens a react-markdown code node's children back into its raw source text. */
function nodeText(node: React.ReactNode): string {
  if (typeof node === 'string') return node;
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (React.isValidElement(node)) return nodeText(node.props.children);
  return '';
}

/** Matches the fragment names used by the Markdown document's table of contents. */
function headingId(children: React.ReactNode): string {
  return nodeText(children)
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-');
}

function markdownHeading(Tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6') {
  return function Heading({ node, children, ...props }) {
    return (
      <Tag {...props} id={headingId(children)}>
        {children}
      </Tag>
    );
  };
}

// This component loads the sooty-blotch-flyspeck markdown with blocks that were originally created in mermaid
// We need to render and commit the files as svg if they ever change. This just reduces a lot of unecessary client or
// build time mermaid rendering, which has a lot of dependency issues, node vs browser API issues, etc.

// We've saved this process as an npm script

const components = {
  h1: markdownHeading('h1'),
  h2: markdownHeading('h2'),
  h3: markdownHeading('h3'),
  h4: markdownHeading('h4'),
  h5: markdownHeading('h5'),
  h6: markdownHeading('h6'),
  table({ node, ...props }) {
    return (
      <div className="markdown-table">
        <table {...props} />
      </div>
    );
  },
  // react-markdown wraps fenced code in <pre><code class="language-x">. Overriding
  // `pre` rather than `code` lets a mermaid fence replace the whole <pre>, instead
  // of nesting a <div> inside it (which is invalid HTML and warns in React).
  pre({ children, ...props }) {
    const [child] = React.Children.toArray(children);

    if (
      React.isValidElement(child) &&
      /\blanguage-mermaid\b/.test(child.props?.className || '')
    ) {
      const chart = nodeText(child.props.children).trim();
      const svg = diagrams[chart];

      // Diagrams are rendered to SVG at build time by `npm run diagrams`. A miss
      // means the SVG is missing or stale, so fall through to the code block
      // rather than showing a diagram that no longer matches the source.
      if (svg) {
        return (
          <div
            className="my-4 overflow-x-auto mermaid-diagram"
            role="img"
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        );
      }
    }

    return <pre {...props}>{children}</pre>;
  },
};

export default function DetailsPage() {
  const { station } = useStations();

  return (
    <Layout station={station}>
      <Seo title={data.title} />
      <article className="container-article max-w-prose mx-auto">
        <Markdown remarkPlugins={[remarkGfm]} components={components}>
          {content}
        </Markdown>
      </article>
    </Layout>
  );
}
